import { useMemo, useEffect, useState, useCallback } from 'react';
import { Row, Col, Form, Input, Space } from 'antd';
import AudiometryTable from './audiometry/AudiometryTable';
import AudiometryChart from './audiometry/AudiometryChart';
import SymbologyTable from './audiometry/SymbologyTable';
import AudiometryAverages from './audiometry/AudiometryAverages';
import NormalForm from './NormalForm';
import { emitter } from '../../../../events';
import { useAudiometryChart } from '../../hooks/useAudiometryChart';
import {
  frequencies,
  sides,
  conductionTypes,
  maskingOptions,
  findByCode,
  completeAudiometryConfig,
} from './audiometry/audiometryConfig';

const normalizeIAResult = (raw) => {
  const str = String(raw ?? '').trim();
  const maskingSuffix = maskingOptions.masking.suffix;
  const noHearingSuffix = maskingOptions.no_hearing.suffix;
  if (/^NR_M(\d+)$/.test(str)) return `${str.slice(4)}${noHearingSuffix}`;
  if (/^NR(\d+)$/.test(str)) return `${str.slice(2)}${noHearingSuffix}`;
  if (str === 'NR') return noHearingSuffix;
  if (/^M(\d+)$/.test(str)) return `${str.slice(1)}${maskingSuffix}`;
  return str;
};

const parseProcedureResult = (result) => {
  const normalizedResult = String(result ?? '').trim();
  if (!normalizedResult) {
    return { value: null, masking: 'none' };
  }

  const sortedOptions = Object.values(maskingOptions).sort(
    (a, b) => b.suffix.length - a.suffix.length,
  );

  for (const option of sortedOptions) {
    if (normalizedResult.endsWith(option.suffix)) {
      const numericPart = normalizedResult.slice(0, -option.suffix.length);
      const value = parseInt(numericPart, 10);
      return { value: isNaN(value) ? null : value, masking: option.value };
    }
  }

  const value = parseInt(normalizedResult, 10);
  return { value: isNaN(value) ? null : value, masking: 'none' };
};

const CompleteAudiometryForm = ({
  fromExternal,
  inputs,
  disableForm,
  form,
  chartRef,
  audiometryState,
  setAudiometry,
  roles,
  fileList,
  loadingFiles,
  status,
  setFileList,
  toDoctor,
  removeDocument,
  convertFile,
  convertError,
  isDICOM,
  handleConvertFile,
  imageUrl,
  setImageUrl,
  setShowAudiometryChartRef,
  countrySettings,
}) => {
  const [chartData, setChartData] = useState([]);
  const [formInitialized, setFormInitialized] = useState(false);
  const { chartImage, showChart, clearChartImage } = useAudiometryChart(inputs);

  const thresholdProcedures = useMemo(() => {
    return (
      inputs?.procedures?.filter(
        (procedure) => procedure?.group === 'thresholds',
      ) || []
    );
  }, [inputs?.procedures]);

  useEffect(() => {
    if (setShowAudiometryChartRef) {
      setShowAudiometryChartRef(showChart);
    }
  }, [setShowAudiometryChartRef, showChart]);

  const parseProcedureCode = (code) => {
    const parts = code?.split('_');
    if (!parts || parts.length < 4) return null;

    const type = findByCode(conductionTypes, parts[1]);
    const side = findByCode(sides, parts[2]);
    const frequency = parts[3];
    const dataIndex = `${type}_${side}`;

    if (!type || !side) return null;

    return { type, side, frequency, dataIndex };
  };

  const procedureMap = useMemo(() => {
    const map = {};
    if (!thresholdProcedures) return map;

    thresholdProcedures.forEach((procedure) => {
      const parsed = parseProcedureCode(procedure.code);
      if (!parsed) return;

      const { frequency, dataIndex } = parsed;

      if (!map[frequency]) map[frequency] = {};
      map[frequency][dataIndex] = {
        id: procedure.id,
        result: procedure.result,
      };
    });

    return map;
  }, [thresholdProcedures]);

  const formValues = Form.useWatch([], form);

  const averageData = useMemo(() => {
    if (!formInitialized) return {};

    if (!fromExternal) {
      return frequencies.reduce((acc, frequency) => {
        acc[frequency] = formValues?.[frequency] || {};
        return acc;
      }, {});
    }

    const externalData = {};

    Object.entries(procedureMap).forEach(([frequency, procedureGroup]) => {
      externalData[frequency] = {};

      Object.entries(procedureGroup).forEach(([key, { result }]) => {
        externalData[frequency][key] = parseProcedureResult(result).value;
      });
    });

    return externalData;
  }, [fromExternal, formValues, procedureMap, formInitialized]);

  const inverseProcedureMap = useMemo(() => {
    const map = {};
    if (!thresholdProcedures) return map;

    thresholdProcedures.forEach((procedure) => {
      const parsed = parseProcedureCode(procedure.code);
      if (!parsed) return;

      const { frequency, dataIndex } = parsed;
      map[procedure.id] = { frequency, dataIndex };
    });

    return map;
  }, [thresholdProcedures]);

  const generateChartData = useCallback((formValues) => {
    return frequencies.map((freq) => {
      const row = formValues[freq] || {};
      return {
        name: freq,
        AC_RE: row.AC_RE,
        AC_LE: row.AC_LE,
        BC_RE: row.BC_RE,
        BC_LE: row.BC_LE,
        AC_RE_masking: row.AC_RE_masking || 'none',
        AC_LE_masking: row.AC_LE_masking || 'none',
        BC_RE_masking: row.BC_RE_masking || 'none',
        BC_LE_masking: row.BC_LE_masking || 'none',
      };
    });
  }, []);

  useEffect(() => {
    if (formInitialized || !Object.keys(procedureMap).length) return;

    const initialFormValues = {};
    const initialProcedures = {};
    frequencies.forEach((freq) => {
      initialFormValues[freq] = {};
      const procedureGroup = procedureMap[freq];
      if (!procedureGroup) return;

      for (const dataIndex in procedureGroup) {
        const { id, result } = procedureGroup[dataIndex];
        initialProcedures[id] = result || '';

        const { value, masking } = parseProcedureResult(result);
        initialFormValues[freq][dataIndex] = value;
        initialFormValues[freq][`${dataIndex}_masking`] = masking;
      }
    });

    form.setFieldsValue({
      ...initialFormValues,
      procedures: initialProcedures,
    });
    setChartData(generateChartData(initialFormValues));
    setFormInitialized(true);
  }, [procedureMap, form, formInitialized, generateChartData]);

  const procedures = Form.useWatch('procedures', form);

  useEffect(() => {
    if (
      !procedures ||
      !Object.keys(inverseProcedureMap).length ||
      !formInitialized
    ) {
      return;
    }

    const formValuesToUpdate = {};

    for (const procId in procedures) {
      const mapping = inverseProcedureMap[procId];
      if (!mapping) continue;

      const { frequency, dataIndex } = mapping;
      const result = procedures[procId];

      const { value, masking } = parseProcedureResult(result);
      const currentFreqValues = form.getFieldValue(frequency) || {};
      const currentNumber = currentFreqValues[dataIndex];
      const currentMasking = currentFreqValues[`${dataIndex}_masking`];

      if (value !== currentNumber || masking !== currentMasking) {
        if (!formValuesToUpdate[frequency]) {
          formValuesToUpdate[frequency] = {};
        }
        formValuesToUpdate[frequency][dataIndex] = value;
        formValuesToUpdate[frequency][`${dataIndex}_masking`] = masking;
      }
    }

    if (Object.keys(formValuesToUpdate).length > 0) {
      form.setFieldsValue(formValuesToUpdate);

      setAudiometry((currentState) => {
        const latestProceduresFromForm = procedures;

        const updatedProcedures = (
          currentState.procedures || inputs.procedures
        ).map((procedure) => {
          if (
            latestProceduresFromForm.hasOwnProperty(procedure.id.toString())
          ) {
            return {
              ...procedure,
              result: latestProceduresFromForm[procedure.id.toString()],
            };
          }
          return procedure;
        });

        return { ...currentState, procedures: updatedProcedures };
      });

      setChartData(generateChartData(form.getFieldsValue()));
    }
  }, [
    procedures,
    inverseProcedureMap,
    form,
    formInitialized,
    generateChartData,
    setAudiometry,
    inputs.procedures,
  ]);

  const handleValueChange = (value, freq, dataIndex) => {
    clearChartImage();
    setTimeout(() => {
      const currentValues = form.getFieldsValue();
      const freqRow = currentValues[freq] || {};

      const baseKey = dataIndex.replace('_masking', '');
      const numberValue = freqRow[baseKey];
      const maskingValue = freqRow[`${baseKey}_masking`] || 'none';

      let finalResult = '';
      const selectedMaskingOption = Object.values(maskingOptions).find(
        (option) => option.value === maskingValue,
      );

      if (numberValue != null && numberValue !== '') {
        finalResult = `${numberValue}${selectedMaskingOption?.suffix || ''}`;
      } else {
        finalResult = selectedMaskingOption?.suffix || '';
      }
      const procedureId = procedureMap[freq]?.[baseKey]?.id;
      if (!procedureId) return;

      const currentProcedures = form.getFieldValue('procedures') || {};

      form.setFieldsValue({
        procedures: {
          ...currentProcedures,
          [procedureId.toString()]: finalResult,
        },
      });

      setAudiometry((currentState) => {
        const updatedProcedures = (
          currentState.procedures || inputs.procedures
        ).map((procedure) =>
          procedure.id === procedureId
            ? { ...procedure, result: finalResult }
            : procedure,
        );
        return { ...currentState, procedures: updatedProcedures };
      });

      setChartData(generateChartData(currentValues));
    }, 0);
  };

  useEffect(() => {
    const IAResultsHandler = ({ data: results }) => {
      const proceduresUpdate = {};

      results.forEach(({ code, result }) => {
        const parsed = parseProcedureCode(code);
        if (!parsed) return;
        const { frequency, dataIndex } = parsed;
        const procedureId = procedureMap[frequency]?.[dataIndex]?.id;
        if (!procedureId) return;

        proceduresUpdate[procedureId] = normalizeIAResult(result);
      });

      if (!Object.keys(proceduresUpdate).length) return;

      clearChartImage();
      form.setFieldsValue({
        procedures: { ...form.getFieldValue('procedures'), ...proceduresUpdate },
      });
    };
    emitter.on('audiometryIAData', IAResultsHandler);
    return () => emitter.off('audiometryIAData', IAResultsHandler);
  }, [form, procedureMap]);

  const normalFormInputs = useMemo(
    () => ({
      ...inputs,
      procedures: inputs.procedures?.filter(
        (procedure) =>
          procedure?.group !== 'thresholds' &&
          procedure?.code !== 'DOCUMENT_AUDI' &&
          procedure?.code !== 'IMAGE_AUDI',
      ),
    }),
    [inputs],
  );

  const fileInputs = useMemo(
    () => ({
      ...inputs,
      procedures: inputs.procedures?.filter((procedure) =>
        ['DOCUMENT_AUDI', 'IMAGE_AUDI'].includes(procedure.code),
      ),
      measurements: [],
      questions: [],
      surveys: [],
    }),
    [inputs],
  );

  return (
    <Row justify='center' gutter={[16, 30]} style={{ marginTop: '20px' }}>
      <Col lg={24} xs={24}>
        <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
          <Col span={24}>
            <Col lg={24}>
              <h2>Archivos</h2>
            </Col>
            <Col
              lg={24}
              className={`col-container${!fromExternal ? ' audiometry-top-inputs' : ''
                }`}
            >
              <NormalForm
                inputs={fileInputs}
                setFileList={setFileList}
                fileList={fileList}
                loadingFiles={loadingFiles}
                toDoctor={toDoctor}
                disableForm={disableForm}
                roles={roles}
                status={status}
                form={form}
                removeDocument={removeDocument}
                convertFile={convertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={handleConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                countrySettings={countrySettings}
                fullInputs={inputs}
                fromExternal={fromExternal}
              />
            </Col>
          </Col>
        </Row>

        <Row justify='space-between' gutter={10}>
          {!fromExternal && (
            <Col
              lg={12}
              xs={24}
              className='col-container audiometry-results-container'
            >
              <Space
                direction='vertical'
                size='middle'
                style={{ width: '100%' }}
              >
                <h2 style={{ margin: 0 }}>Datos</h2>
                <AudiometryTable
                  form={form}
                  dataSource={frequencies}
                  firstColumnTitle='F (Hz)'
                  columnsConfig={completeAudiometryConfig.columns}
                  onValueChange={handleValueChange}
                  disableForm={disableForm}
                  showMasking={true}
                  isInteger={true}
                />
              </Space>
            </Col>
          )}
          <Col lg={fromExternal ? 24 : 11} xs={24}>
            <Row gutter={[0, 24]}>
              {!fromExternal && (
                <Col span={24}>
                  <SymbologyTable
                    columnsConfig={completeAudiometryConfig.columns}
                  />
                </Col>
              )}
              <Col
                lg={fromExternal ? 24 : 24}
                xs={24}
                className='audiometry-chart-container full-width-chart'
              >
                {!fromExternal ? (
                  <AudiometryChart
                    chartRef={chartRef}
                    data={chartData}
                    seriesConfig={completeAudiometryConfig.series}
                  />
                ) : (
                  <img src={chartImage} alt='Gráfico de audiometría' />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col lg={24} xs={24}>
            <AudiometryAverages
              averageData={averageData}
              config={completeAudiometryConfig.averages}
              fromExternal={fromExternal}
            />
          </Col>
        </Row>
        {thresholdProcedures.map((proc) => (
          <Form.Item
            key={proc.id}
            name={['procedures', proc.id.toString()]}
            noStyle
          >
            <Input type='hidden' />
          </Form.Item>
        ))}

        <Row className='mt-20' justify='space-between'>
          <Col lg={fromExternal ? 24 : 10} className='col-container'>
            <Row justify='center'>
              <Col lg={22}>
                <h2>Resultados</h2>
              </Col>
            </Row>
            <Row className='results-row mt-20' />
            <Row className='mt-20' justify='center'>
              <Col lg={22}>
                <NormalForm
                  form={form}
                  inputs={normalFormInputs}
                  disableForm={disableForm}
                  roles={roles}
                  status={status}
                  loadingFiles={loadingFiles}
                  setFileList={setFileList}
                  fileList={fileList}
                  toDoctor={toDoctor}
                  removeDocument={removeDocument}
                  convertFile={convertFile}
                  convertError={convertError}
                  isDICOM={isDICOM}
                  handleConvertFile={handleConvertFile}
                  imageUrl={imageUrl}
                  setImageUrl={setImageUrl}
                  countrySettings={countrySettings}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default CompleteAudiometryForm;
